import { Empty } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import type { BaseBreakPointConfigProtocol, FilterBlockAbstract, FilterBlockValue, VariableADTvalue } from '@lighthouse/core'
import type { FilterInteractionStyles, FilterStyles } from '@lighthouse/shared'
import {
    CenteredWrapper,
    flexMinSizeStyle,
    getCursorStyle,
    TEXT_FONT_NORMAL,
    useApplicationContext,
    useFilterInteractionStyles,
    useFilterStyles,
    useLanguageContext
} from '@lighthouse/shared'
import { camelToKebab } from '@lighthouse/tools'
import React, { memo, useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'

import { AnimationDecorators } from '../../components'
import { useEffectiveAnchorId } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { DesktopFilter } from './DesktopFilter'
import { MobileFilter } from './MobileFilter'
import type { FilterItemProps } from './types'

interface FilterBlockProps
    extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'>,
        Pick<
            FilterItemProps,
            'dataSourceList' | 'onChange' | 'onFetchTextOptions' | 'onFetchCascadeOptions' | 'useParseVariableWithSubscription'
        > {
    blockData: FilterBlockAbstract
    filterData: FilterBlockValue
    noFilterLabel?: string
    onRenderLabel?: (v: VariableADTvalue | undefined) => string
}

const SCxFilterBlockContainer = styled(AnimationDecorators)`
    width: 100%;
    min-height: 32px;
    padding: var(--block-padding);
    height: 100%;
`

const SCxStyleContent = styled.div<{
    fontFamily?: string
    filterStyle?: FilterStyles
    filterInteractionStyle?: FilterInteractionStyles
}>`
    ${({ fontFamily }) =>
        fontFamily &&
        fontFamily !== TEXT_FONT_NORMAL &&
        css`
            font-family: ${fontFamily};
        `}

    /* filter */
    ${({ filterStyle }) =>
        filterStyle?.filter &&
        css`
            & [data-filter-input] {
                ${Object.entries(filterStyle.filter)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

    ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.filter?.hoverStyle &&
        css`
            & [data-filter-input][data-filter-active='false'][data-filter-disabled='false']:hover {
                ${Object.entries(filterInteractionStyle.filter.hoverStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

        ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.filter?.activeStyle &&
        css`
            & [data-filter-input][data-filter-active='true'] {
                ${Object.entries(filterInteractionStyle.filter.activeStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

        ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.filter?.disabledStyle &&
        css`
            & [data-filter-input][data-filter-disabled='true'] {
                ${Object.entries(filterInteractionStyle.filter.disabledStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

    /* icon */

    ${({ filterStyle }) =>
        filterStyle?.icon &&
        css`
            & [data-filter-input] [data-filter-icon] {
                ${Object.entries(filterStyle.icon)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}


           ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.icon?.hoverStyle &&
        css`
            & [data-filter-active='false'][data-filter-disabled='false'] [data-filter-icon]:hover {
                ${Object.entries(filterInteractionStyle.icon.hoverStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}



           ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.icon?.activeStyle &&
        css`
            & [data-filter-active='true'] [data-filter-icon] {
                ${Object.entries(filterInteractionStyle.icon.activeStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}


           ${({ filterInteractionStyle }) =>
        filterInteractionStyle?.icon?.disabledStyle &&
        css`
            & [data-filter-disabled='true'] [data-filter-icon] {
                ${Object.entries(filterInteractionStyle.icon.disabledStyle)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}
`

const EmptyImage = styled.img`
    width: 140px;
    height: 144px;
`

const FilterBlock: React.FC<FilterBlockProps> = ({
    dataSourceList,
    blockData,
    filterData,
    noFilterLabel,
    onChange,
    onFetchTextOptions,
    onFetchCascadeOptions,
    onRenderLabel,
    useParseVariableWithSubscription,
    ...rest
}) => {
    const { id, config, title } = blockData
    const { appId = '', isMobile } = useApplicationContext()
    const emptyImageUrl = getAssetUrl('empty', 'empty_filter.png')
    const { filterItems, mode, labelWidth, showLabel, breakPoint, cursor, anchor } = config
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const Component = useMemo(() => (isMobile ? MobileFilter : DesktopFilter), [isMobile])
    const { convertTextByLanguage } = useLanguageContext()

    const { fontFamily, isWrap, labelColor } = useMemo(() => {
        return {
            fontFamily: breakPoint?.font?.fontFamily,
            isWrap: breakPoint?.layout?.wrap,
            labelColor: breakPoint?.design?.labelColor
        }
    }, [breakPoint])

    const filterStyle = useFilterStyles(breakPoint, {
        parseVariableImage: onRenderLabel
    })
    const filterInteractionStyle = useFilterInteractionStyles(breakPoint, {
        parseVariableImage: onRenderLabel
    })

    useEffect(() => {
        if (!fontFamily || fontFamily === TEXT_FONT_NORMAL) {
            return
        }
        document.fonts.ready
            .then(async ffs => {
                const fontFaceList = [...ffs.values()]
                const isLoaded = fontFaceList.some(item => item.family === fontFamily)
                if (!isLoaded) {
                    const fontUrl = `url(${getAssetUrl('font', `${fontFamily}.woff2`)})`
                    const newFont = new FontFace(fontFamily, fontUrl)
                    newFont._snapdomSrc = `${getAssetUrl('font', `${fontFamily}.woff2`)}`
                    await newFont.load()
                    ffs.add(newFont)
                }
            })

            .catch(console.warn)
    }, [fontFamily])

    return (
        <SCxFilterBlockContainer
            blockId={id}
            blockName={title}
            animation={breakPoint?.animation}
            id={effectiveAnchorId}
            data-block-type="chartView"
            {...rest}
            style={{ ...flexMinSizeStyle, overflow: breakPoint.size.overflow, ...rest.style, ...getCursorStyle(appId, cursor) }}
        >
            <SCxStyleContent
                fontFamily={fontFamily}
                filterStyle={filterStyle}
                filterInteractionStyle={filterInteractionStyle}
                onClick={e => e.stopPropagation()}
            >
                {filterItems.length === 0 && (
                    <CenteredWrapper style={{ padding: 24 }}>
                        <Empty
                            icon={<EmptyImage src={emptyImageUrl} alt={convertTextByLanguage('pleasesAddFilter')} />}
                            description={convertTextByLanguage('pleasesAddFilter')}
                        />
                    </CenteredWrapper>
                )}
                <Component
                    list={filterItems}
                    mode={mode}
                    isWrap={isWrap}
                    showLabel={showLabel}
                    labelWidth={labelWidth}
                    labelColor={labelColor}
                    noFilterLabel={noFilterLabel}
                    filterData={filterData}
                    dataSourceList={dataSourceList}
                    onFetchTextOptions={onFetchTextOptions}
                    onFetchCascadeOptions={onFetchCascadeOptions}
                    useParseVariableWithSubscription={useParseVariableWithSubscription}
                    onChange={onChange}
                />
            </SCxStyleContent>
        </SCxFilterBlockContainer>
    )
}

export default memo(FilterBlock)
